import { useEffect, useRef } from "react";

/**
 * TechArtefacts — Industry-themed animated background for ClomedixTech (IT / digital software services).
 * Replaces the previous generic floating orbs with:
 *  - Code brackets { } < />
 *  - Tiny SVG dashboard tiles (bar chart, sparkline)
 *  - Circuit / gear glyphs
 *  - AI neural-node blobs with connecting lines
 *  - Cloud + shield glyphs
 *  - Moving data-flow curves
 * All in the brand palette (blue → purple → pink). Continuous CSS animation + mouse parallax.
 */
export const TechArtefacts = () => {
  const wrap = useRef(null);

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const nodes = el.querySelectorAll("[data-parallax]");
    let raf = 0;
    let mx = 0, my = 0, tx = 0, ty = 0;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      mx = (e.clientX - r.left) / r.width - 0.5;
      my = (e.clientY - r.top) / r.height - 0.5;
    };
    const tick = () => {
      tx += (mx - tx) * 0.05;
      ty += (my - ty) * 0.05;
      nodes.forEach((n) => {
        const d = parseFloat(n.dataset.parallax || 12);
        n.style.transform = `translate3d(${tx * d}px, ${ty * d}px, 0)`;
      });
      raf = requestAnimationFrame(tick);
    };
    el.addEventListener("mousemove", onMove);
    raf = requestAnimationFrame(tick);
    return () => { el.removeEventListener("mousemove", onMove); cancelAnimationFrame(raf); };
  }, []);

  return (
    <div ref={wrap} className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      {/* Ambient palette glows (blue → purple → pink) */}
      <div className="absolute -top-24 -left-20 w-[440px] h-[440px] rounded-full opacity-40 blur-3xl animate-float-slow"
           data-parallax="18"
           style={{ background: "radial-gradient(circle at 30% 30%, rgba(37,99,235,0.75), transparent 60%)" }} />
      <div className="absolute top-20 right-[-100px] w-[420px] h-[420px] rounded-full opacity-40 blur-3xl animate-float-medium"
           data-parallax="24"
           style={{ background: "radial-gradient(circle, rgba(147,51,234,0.7), transparent 60%)", animationDelay: "1.2s" }} />
      <div className="absolute bottom-[-120px] left-1/3 w-[480px] h-[480px] rounded-full opacity-40 blur-3xl animate-float-slow"
           data-parallax="14"
           style={{ background: "radial-gradient(circle at 60% 40%, rgba(219,39,119,0.7), transparent 60%)", animationDelay: "0.7s" }} />

      {/* Data-flow curves — SVG paths animating along the background */}
      <svg className="absolute inset-0 w-full h-full opacity-30" viewBox="0 0 1200 700" fill="none" preserveAspectRatio="none" data-parallax="6">
        <defs>
          <linearGradient id="tech-line" x1="0" x2="1">
            <stop offset="0%" stopColor="#2563EB" stopOpacity="0" />
            <stop offset="50%" stopColor="#9333EA" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#DB2777" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="tech-line-2" x1="0" x2="1">
            <stop offset="0%" stopColor="#DB2777" stopOpacity="0" />
            <stop offset="60%" stopColor="#2563EB" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#9333EA" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d="M -50 200 C 220 120, 480 280, 720 180 S 1100 260, 1300 160" stroke="url(#tech-line)" strokeWidth="2" strokeDasharray="6 10">
          <animate attributeName="stroke-dashoffset" from="0" to="-160" dur="7s" repeatCount="indefinite" />
        </path>
        <path d="M -50 520 C 260 440, 500 620, 780 500 S 1080 560, 1300 480" stroke="url(#tech-line-2)" strokeWidth="1.6" strokeDasharray="4 8">
          <animate attributeName="stroke-dashoffset" from="0" to="120" dur="9s" repeatCount="indefinite" />
        </path>
        <path d="M -50 360 C 260 300, 520 430, 780 340 S 1080 380, 1300 320" stroke="url(#tech-line)" strokeWidth="1.2" strokeDasharray="2 12" opacity="0.7">
          <animate attributeName="stroke-dashoffset" from="0" to="-200" dur="12s" repeatCount="indefinite" />
        </path>
      </svg>

      {/* Code bracket — top left */}
      <div className="absolute animate-float-slow" style={{ top: "12%", left: "6%", animationDuration: "7s" }} data-parallax="14">
        <div className="glass-card rounded-2xl px-4 py-3 soft-shadow font-mono font-bold text-lg leading-none"
             style={{ background: "linear-gradient(135deg, rgba(37,99,235,0.14), rgba(147,51,234,0.14))" }}>
          <span className="text-brand-blue">{`{ `}</span>
          <span className="text-brand-purple">code</span>
          <span className="text-brand-blue">{` }`}</span>
        </div>
      </div>

      {/* Code bracket — right */}
      <div className="absolute animate-float-medium" style={{ top: "58%", right: "4%", animationDelay: "1.5s" }} data-parallax="18">
        <div className="glass-card rounded-2xl px-4 py-3 soft-shadow font-mono font-bold text-base"
             style={{ background: "linear-gradient(135deg, rgba(219,39,119,0.14), rgba(147,51,234,0.14))" }}>
          <span className="text-brand-pink">{`<`}</span>
          <span className="text-brand-purple">ai</span>
          <span className="text-brand-pink">{` />`}</span>
        </div>
      </div>

      {/* Mini dashboard tile — sparkline */}
      <div className="absolute animate-float-slow" style={{ top: "22%", right: "8%", animationDelay: "0.4s" }} data-parallax="20">
        <div className="glass-card rounded-2xl p-3 soft-shadow w-[130px]">
          <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Deploys · today</div>
          <div className="mt-1 text-lg font-heading font-extrabold text-slate-900">+38%</div>
          <svg viewBox="0 0 100 30" className="w-full h-6 mt-1">
            <polyline points="0,22 15,18 30,20 45,10 60,14 75,6 90,8 100,3"
                      fill="none" stroke="url(#tech-line)" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
      </div>

      {/* Mini dashboard tile — bar chart */}
      <div className="absolute animate-float-medium" style={{ bottom: "18%", left: "4%", animationDelay: "1s" }} data-parallax="16">
        <div className="glass-card rounded-2xl p-3 soft-shadow w-[130px]">
          <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Uptime · 30d</div>
          <div className="mt-1 flex items-end gap-1 h-8">
            {[40,60,80,55,70,90,65,85,95,75].map((h,i)=>(
              <span key={i} className="flex-1 rounded-sm"
                    style={{ height: `${h}%`, background: `linear-gradient(180deg,#2563EB,#9333EA)`, opacity: 0.35 + (i/12) }} />
            ))}
          </div>
        </div>
      </div>

      {/* AI neural node cluster */}
      <div className="absolute animate-float-slow" style={{ top: "40%", left: "-2%", animationDelay: "0.8s" }} data-parallax="10">
        <svg width="130" height="110" viewBox="0 0 130 110">
          <defs>
            <radialGradient id="node-glow" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0%" stopColor="#9333EA" stopOpacity="1" />
              <stop offset="100%" stopColor="#9333EA" stopOpacity="0" />
            </radialGradient>
          </defs>
          <g stroke="#9333EA" strokeOpacity="0.4" strokeWidth="1">
            <line x1="25" y1="30" x2="70" y2="15" />
            <line x1="25" y1="30" x2="65" y2="55" />
            <line x1="70" y1="15" x2="110" y2="45" />
            <line x1="65" y1="55" x2="110" y2="45" />
            <line x1="65" y1="55" x2="45" y2="90" />
            <line x1="110" y1="45" x2="95" y2="85" />
            <line x1="45" y1="90" x2="95" y2="85" />
          </g>
          {[[25,30],[70,15],[65,55],[110,45],[45,90],[95,85]].map(([x,y],i)=>(
            <g key={i}>
              <circle cx={x} cy={y} r="16" fill="url(#node-glow)" opacity="0.4"/>
              <circle cx={x} cy={y} r="4" fill="#9333EA">
                <animate attributeName="r" values="4;6;4" dur={`${2+i*0.3}s`} repeatCount="indefinite" />
              </circle>
            </g>
          ))}
        </svg>
      </div>

      {/* Rotating circuit / gear ring */}
      <div className="absolute" style={{ top: "8%", right: "36%" }} data-parallax="12">
        <svg width="80" height="80" viewBox="0 0 80 80" className="animate-spin-slow" style={{ animation: "spin-slow 22s linear infinite" }}>
          <circle cx="40" cy="40" r="26" fill="none" stroke="#2563EB" strokeOpacity="0.5" strokeWidth="1" strokeDasharray="3 6"/>
          <circle cx="40" cy="40" r="18" fill="none" stroke="#DB2777" strokeOpacity="0.5" strokeWidth="1" strokeDasharray="2 5"/>
          {Array.from({length:8}).map((_,i)=>{
            const a = (i/8)*Math.PI*2;
            return <rect key={i} x={38} y={4} width={4} height={8}
                         transform={`rotate(${(i/8)*360} 40 40)`} fill="#9333EA" opacity="0.6"/>;
          })}
          <circle cx="40" cy="40" r="5" fill="#9333EA"/>
        </svg>
      </div>

      {/* Cloud glyph pill */}
      <div className="absolute animate-float-medium" style={{ bottom: "10%", right: "22%", animationDelay: "0.5s" }} data-parallax="15">
        <div className="glass-card rounded-full px-3 py-2 soft-shadow inline-flex items-center gap-2"
             style={{ background: "linear-gradient(135deg, rgba(56,189,248,0.18), rgba(147,51,234,0.18))" }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M6 16 A4 4 0 0 1 6 8 A5 5 0 0 1 16 8 A4 4 0 0 1 18 16Z"
                  stroke="#2563EB" strokeWidth="1.6" fill="rgba(37,99,235,0.15)"/>
          </svg>
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-700">Cloud</span>
        </div>
      </div>

      {/* Shield glyph pill */}
      <div className="absolute animate-float-slow" style={{ top: "70%", right: "40%", animationDelay: "1.1s" }} data-parallax="14">
        <div className="glass-card rounded-full px-3 py-2 soft-shadow inline-flex items-center gap-2"
             style={{ background: "linear-gradient(135deg, rgba(147,51,234,0.18), rgba(219,39,119,0.18))" }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M12 3 L20 6 V12 C20 17 16 20.5 12 22 C8 20.5 4 17 4 12 V6 Z"
                  stroke="#9333EA" strokeWidth="1.6" fill="rgba(147,51,234,0.15)"/>
            <path d="M9 12 L11.5 14.5 L15.5 10.5" stroke="#DB2777" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-700">Secure</span>
        </div>
      </div>

      {/* Small twinkling data particles */}
      {Array.from({ length: 12 }).map((_, i) => (
        <span
          key={i}
          className="absolute rounded-full"
          data-parallax={4 + (i % 5) * 3}
          style={{
            width: `${5 + (i % 4) * 2}px`,
            height: `${5 + (i % 4) * 2}px`,
            top: `${(i * 43) % 90 + 5}%`,
            left: `${(i * 79) % 88 + 6}%`,
            background: ["#2563EB", "#9333EA", "#DB2777"][i % 3],
            opacity: 0.4,
            filter: "blur(0.5px)",
            boxShadow: `0 0 12px ${["#2563EB", "#9333EA", "#DB2777"][i % 3]}`,
            animation: `twinkle ${3 + (i % 4)}s ease-in-out ${i * 0.25}s infinite`,
          }}
        />
      ))}
    </div>
  );
};
